import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { adminFinance } from '../api/finance'
import type { AdminOperationRow, Page, StripeLogRow } from '../api/financeTypes'
import { useFinance } from './financeContext'
import { AdminOpsTable, StripeLogTable } from './logTables'
import { Card, FinanceErrorNotice, HelpLabel, PageHead } from './ui'

/** Keyset-paginated list: loads the first page on scope/filter change and
 * appends older pages ("Régebbiek") using `next_before`. */
function usePaged<T>(load: (before?: string) => Promise<Page<T>>, deps: unknown[]) {
  const [rows, setRows] = useState<T[]>([])
  const [next, setNext] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    let live = true
    setLoading(true)
    setError(null)
    load().then(
      (p) => {
        if (!live) return
        setRows(p.items)
        setNext(p.next_before)
        setLoading(false)
      },
      (e) => {
        if (!live) return
        setError(e)
        setLoading(false)
      },
    )
    return () => {
      live = false
    }
  }, deps)
  /* eslint-enable react-hooks/exhaustive-deps */
  const more = async () => {
    if (!next) return
    setLoading(true)
    try {
      const p = await load(next)
      setRows((r) => [...r, ...p.items])
      setNext(p.next_before)
    } catch (e) {
      setError(e)
    }
    setLoading(false)
  }
  return { rows, next, error, loading, more }
}

function Pager({ next, loading, more }: { next: string | null; loading: boolean; more: () => void }) {
  if (!next) return <p className="af-sub">Ez volt az összes sor.</p>
  return (
    <button type="button" className="af-btn" disabled={loading} onClick={more}>
      {loading ? 'Betöltés…' : 'Régebbiek'}
    </button>
  )
}

export function StripeLogPage() {
  const fin = useFinance()
  const [params, setParams] = useSearchParams()
  const email = params.get('email') ?? ''
  const result = params.get('result') ?? ''
  const [draft, setDraft] = useState(email)
  const paged = usePaged<StripeLogRow>(
    (before) => adminFinance.stripeLog(fin.projectId, { email: email || undefined, result: result || undefined, limit: 50, before }),
    [fin.projectId, email, result],
  )
  const apply = (next: Record<string, string>) => {
    const p = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v)
      else p.delete(k)
    }
    setParams(p)
  }
  return (
    <>
      <PageHead title="Stripe-napló" sub="Minden Stripe-hívás egy sor · kártyaadat nincs tárolva">
        <form
          className="af-search"
          onSubmit={(e) => {
            e.preventDefault()
            apply({ email: draft.trim() })
          }}
        >
          <label>
            Felhasználó
            <input type="search" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="e-mail" />
          </label>
          <label>
            Eredmény
            <select value={result} onChange={(e) => apply({ result: e.target.value })}>
              <option value="">mind</option>
              <option value="succeeded">succeeded</option>
              <option value="declined">declined</option>
              <option value="unknown">unknown</option>
            </select>
          </label>
        </form>
      </PageHead>
      <Card className="af-stack">
        <HelpLabel helpKey="stripeLog" as="h2">
          Hívások
        </HelpLabel>
        {paged.error ? <FinanceErrorNotice error={paged.error} /> : <StripeLogTable rows={paged.rows} withEmail />}
        {!paged.error && !(paged.loading && paged.rows.length === 0) && <Pager next={paged.next} loading={paged.loading} more={paged.more} />}
        {paged.loading && paged.rows.length === 0 && !paged.error && <p className="portal-text-muted">Betöltés…</p>}
      </Card>
    </>
  )
}

export function AdminOperationsPage() {
  const fin = useFinance()
  const [admin, setAdmin] = useState('')
  const paged = usePaged<AdminOperationRow>((before) => adminFinance.adminOperations(fin.projectId, { admin: admin || undefined, limit: 50, before }), [fin.projectId, admin])
  return (
    <>
      <PageHead title="Admin-műveletek" sub="Ki, mikor, mit csinált · pénzügyi rekord nem törlődik">
        <label className="af-search">
          Admin
          <input type="search" value={admin} onChange={(e) => setAdmin(e.target.value)} placeholder="név" />
        </label>
      </PageHead>
      <Card className="af-stack">
        <HelpLabel helpKey="adminOps" as="h2">
          Műveletek
        </HelpLabel>
        {paged.error ? <FinanceErrorNotice error={paged.error} /> : <AdminOpsTable rows={paged.rows} />}
        {!paged.error && !(paged.loading && paged.rows.length === 0) && <Pager next={paged.next} loading={paged.loading} more={paged.more} />}
        {paged.loading && paged.rows.length === 0 && !paged.error && <p className="portal-text-muted">Betöltés…</p>}
      </Card>
    </>
  )
}
